import { useState, useEffect, useMemo } from 'react';
import caseStudiesService from '../services/caseStudiesService';

// Custom hook for fetching all case studies
export const useCaseStudies = (filters = {}) => {
  const [caseStudies, setCaseStudies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [meta, setMeta] = useState({});

  useEffect(() => {
    const fetchCaseStudies = async () => {
      setLoading(true);
      setError(null);
      
      try {
        const result = await caseStudiesService.getAllCaseStudies();
        
              
        if (result.success) {
          setCaseStudies(result.data);
          setMeta(result.meta);
        } else {
          setError(result.error);
          setCaseStudies([]);
        }
      } catch (err) {
        setError(err.message);
        setCaseStudies([]);
      } finally {
        setLoading(false);
      }
    };

    fetchCaseStudies();
  }, []); // Remove dependency on filters to prevent re-renders

  return { caseStudies, loading, error, meta };
};

// Custom hook for fetching a single case study by slug
export const useCaseStudy = (slug) => {
  const [caseStudy, setCaseStudy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!slug) {
      setLoading(false);
      return;
    }

    const fetchCaseStudy = async () => {
      setLoading(true);
      setError(null);
      
      try {
        const result = await caseStudiesService.getCaseStudyBySlug(slug);
        
        if (result.success) {
          setCaseStudy(result.data);
        } else {
          setError(result.error);
          setCaseStudy(null);
        }
      } catch (err) {
        setError(err.message);
        setCaseStudy(null);
      } finally {
        setLoading(false);
      }
    };

    fetchCaseStudy();
  }, [slug]);

  return { caseStudy, loading, error };
};

// Custom hook for fetching case studies by category
export const useCaseStudiesByCategory = (category, filters = {}) => {
  const [caseStudies, setCaseStudies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [meta, setMeta] = useState({});

  const filtersKey = useMemo(() => JSON.stringify(filters), [filters]);

  useEffect(() => {
    if (!category) {
      setLoading(false);
      return;
    }

    const fetchCaseStudies = async () => {
      setLoading(true);
      setError(null);
      
      try {
        const result = await caseStudiesService.getAllCaseStudies({
          ...filters,
          'filters[categories][categoryName][$eq]': category
        });
        
        if (result.success) {
          setCaseStudies(result.data);
          setMeta(result.meta);
        } else {
          setError(result.error);
          setCaseStudies([]);
        }
      } catch (err) {
        setError(err.message);
        setCaseStudies([]);
      } finally {
        setLoading(false);
      }
    };

    fetchCaseStudies();
  }, [category, filtersKey, filters]);

  return { caseStudies, loading, error, meta };
};

// Custom hook for fetching featured case studies
export const useFeaturedCaseStudies = (filters = {}) => {
  const [caseStudies, setCaseStudies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [meta, setMeta] = useState({});

  const filtersKey = useMemo(() => JSON.stringify(filters), [filters]);

  useEffect(() => {
    const fetchFeaturedCaseStudies = async () => {
      setLoading(true);
      setError(null);
      
      try {
        const result = await caseStudiesService.getAllCaseStudies({
          'filters[featured][$eq]': true
        });
        
        if (result.success) {
          setCaseStudies(result.data);
          setMeta(result.meta);
        } else {
          setError(result.error);
          setCaseStudies([]);
        }
      } catch (err) {
        setError(err.message);
        setCaseStudies([]);
      } finally {
        setLoading(false);
      }
    };

    fetchFeaturedCaseStudies();
  }, [filtersKey, filters]);

  return { caseStudies, loading, error, meta };
};

// Custom hook with pagination
export const useCaseStudiesWithPagination = (page = 1, pageSize = 10, filters = {}) => {
  const [caseStudies, setCaseStudies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [meta, setMeta] = useState({});

  const filtersKey = useMemo(() => JSON.stringify(filters), [filters]);

  useEffect(() => {
    const fetchCaseStudies = async () => {
      setLoading(true);
      setError(null);
      
      try {
        const paginationFilters = {
          'pagination[page]': page,
          'pagination[pageSize]': pageSize,
          ...filters
        };
        
        const result = await caseStudiesService.getAllCaseStudies(paginationFilters);
        
        if (result.success) {
          setCaseStudies(result.data);
          setMeta(result.meta);
        } else {
          setError(result.error);
          setCaseStudies([]);
        }
      } catch (err) {
        setError(err.message);
        setCaseStudies([]);
      } finally {
        setLoading(false);
      }
    };

    fetchCaseStudies();
  }, [page, pageSize, filtersKey, filters]);

  return { caseStudies, loading, error, meta };
};
